'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { getChainAddresses } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isAddressEqual } from 'viem';
import { useActionFlowReview } from '@/components/ActionFlow/useActionFlowReview';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { BUNDLE_NOT_AUTHORIZED_MESSAGE, EXIT_BUNDLE_NOT_ENABLED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable, TextError } from '@/components/common/Error';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import { WrapSegment } from '@/components/common/WrapSegment';
import { useVaultV2DepositLogic } from '@/components/VaultPage/VaultV2/hooks/useVaultV2DepositLogic';
import { vaultV2DepositReviewDialogs } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/vaultV2DepositReviewDialogs';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import { orpc } from '@/lib/orpc/client';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { toVaultV2PositionManagerData } from '../toVaultV2PositionManagerData';
import VaultV2TransactionInfoCard from '../VaultV2TransactionInfoCard';
import VaultV2PositionManagerDepositDialogLoading from './VaultV2PositionManagerDepositDialogLoading';
interface VaultV2PositionManagerDepositDialogContentProps {
  vaultAddress: Address;
  chainId: ChainId;
  /** The vault's asset, when the caller knows it before the vault data loads. */
  assetAddress?: Address;
}
const VaultV2PositionManagerDepositDialogContent = t0 => {
  const $ = _c(92);
  const {
    vaultAddress,
    chainId,
    assetAddress
  } = t0;
  const [isInputPending, setIsInputPending] = useState(false);
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== toggleDialog) {
    t1 = {
      dialogOptions: POSITION_MANAGER_DIALOG_OPTIONS,
      onCompletedExit: () => toggleDialog(false)
    };
    $[0] = toggleDialog;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    open,
    element,
    isOpen: isReviewing
  } = useActionFlowReview("vaultV2Deposit", vaultV2DepositReviewDialogs, t1);
  const {
    account
  } = useWeb3Context();
  const {
    vaultV2WrapDeposit: isWrapDepositEnabled
  } = useFeatureFlags();
  let t2;
  if ($[2] !== account || $[3] !== assetAddress || $[4] !== chainId || $[5] !== isWrapDepositEnabled) {
    const {
      wNative
    } = getChainAddresses(chainId);
    t2 = isWrapDepositEnabled && account.isConnected && !!assetAddress && !!wNative && isAddressEqual(assetAddress, wNative);
    $[2] = account;
    $[3] = assetAddress;
    $[4] = chainId;
    $[5] = isWrapDepositEnabled;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const reservesWrapRow = t2;
  let t3;
  if ($[7] !== chainId || $[8] !== vaultAddress) {
    t3 = orpc.vaultV2.positionManager.queryOptions({
      input: {
        chainId,
        address: vaultAddress
      }
    });
    $[7] = chainId;
    $[8] = vaultAddress;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: positionManager,
    isPending: isQueryLoading,
    error: queryError
  } = useQuery(t3);
  let t4;
  let vaultData;
  if ($[10] !== chainId || $[11] !== positionManager) {
    vaultData = positionManager ? toVaultV2PositionManagerData(positionManager, chainId) : undefined;
    t4 = getParsedValue(vaultData?.asset, parseApiAsset);
    $[10] = chainId;
    $[11] = positionManager;
    $[12] = t4;
    $[13] = vaultData;
  } else {
    t4 = $[12];
    vaultData = $[13];
  }
  const vaultAsset = t4;
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultBundlesV1", vaultAsset?.address, chainId);
  const {
    canTransfer: exitBundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultExitBundlesV1", vaultAsset?.address, chainId);
  let t5;
  if ($[14] !== isReviewing || $[15] !== vaultAddress || $[16] !== vaultData) {
    t5 = {
      vaultData,
      vaultAddress,
      isReviewing
    };
    $[14] = isReviewing;
    $[15] = vaultAddress;
    $[16] = vaultData;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxDeposit,
    finalPosition,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    disabledLabel,
    userVaultAssets,
    error,
    resetInput,
    refetchPosition,
    refetchVaultData,
    erc20Amount,
    nativeAmount,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    isNativeBalancePending
  } = useVaultV2DepositLogic(t5);
  let t6;
  if ($[18] !== erc20Amount || $[19] !== finalPosition || $[20] !== inputAmount || $[21] !== nativeAmount || $[22] !== open || $[23] !== refetchPosition || $[24] !== refetchVaultData || $[25] !== resetInput || $[26] !== userVaultAssets || $[27] !== vaultAddress || $[28] !== vaultAsset) {
    t6 = () => {
      if (!isDefined(userVaultAssets) || !vaultAsset) {
        throw new Error("Missing data to open review dialog");
      }
      open({
        vaultAddress,
        inputAmount,
        erc20Amount,
        nativeAmount,
        vaultAsset,
        userPosition: userVaultAssets,
        finalPosition,
        onTxSuccess: resetInput,
        refetchPosition,
        refetchVaultData,
        entryPoint: "position_manager",
        recapSource: "positionManager"
      });
    };
    $[18] = erc20Amount;
    $[19] = finalPosition;
    $[20] = inputAmount;
    $[21] = nativeAmount;
    $[22] = open;
    $[23] = refetchPosition;
    $[24] = refetchVaultData;
    $[25] = resetInput;
    $[26] = userVaultAssets;
    $[27] = vaultAddress;
    $[28] = vaultAsset;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const openReviewDialog = t6;
  if (isQueryLoading) {
    let t7;
    if ($[30] !== reservesWrapRow) {
      t7 = <VaultV2PositionManagerDepositDialogLoading reservesWrapRow={reservesWrapRow} />;
      $[30] = reservesWrapRow;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t8;
    if ($[32] !== element || $[33] !== t7) {
      t8 = <>{element}{t7}</>;
      $[32] = element;
      $[33] = t7;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    return t8;
  }
  if (queryError || !vaultData || !vaultAsset) {
    let t7;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ErrorServiceUnavailable minHeight="400px" />;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    let t8;
    if ($[36] !== element) {
      t8 = <>{element}{t7}</>;
      $[36] = element;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    return t8;
  }
  const t7 = `Deposit ${vaultAsset.symbol}`;
  const t8 = maxDeposit?.value;
  let t9;
  if ($[38] !== inputAmount || $[39] !== isMax || $[40] !== isMaxLoading || $[41] !== onMaxClick || $[42] !== setInputAmount || $[43] !== t7 || $[44] !== t8 || $[45] !== vaultAsset) {
    t9 = <CompactAssetInput header={t7} amount={inputAmount} token={vaultAsset} onChange={setInputAmount} onPendingChange={setIsInputPending} maxValue={t8} onMaxClick={onMaxClick} isMax={isMax} loading={isMaxLoading} />;
    $[38] = inputAmount;
    $[39] = isMax;
    $[40] = isMaxLoading;
    $[41] = onMaxClick;
    $[42] = setInputAmount;
    $[43] = t7;
    $[44] = t8;
    $[45] = vaultAsset;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== error.userBalanceError) {
    t10 = error.userBalanceError && <div className="flex justify-end"><TextError message="Error fetching wallet balance." messageTooltip={error.userBalanceError.message} /></div>;
    $[47] = error.userBalanceError;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== error.nativeBalanceError) {
    t11 = error.nativeBalanceError && <div className="flex justify-end"><TextError message="Error fetching native balance." messageTooltip={error.nativeBalanceError.message} /></div>;
    $[49] = error.nativeBalanceError;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== error.userVaultPositionError) {
    t12 = error.userVaultPositionError && <div className="flex justify-end"><TextError message="Error fetching vault position." messageTooltip={error.userVaultPositionError.message} /></div>;
    $[51] = error.userVaultPositionError;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t9) {
    t13 = <div className="gap-2xs flex flex-col">{t9}{t10}{t11}{t12}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t9;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  const t14 = vaultAsset?.symbol;
  let t15;
  if ($[58] !== isNativeBalancePending || $[59] !== isWNativeVault || $[60] !== isWrapping || $[61] !== isWrappingToggleLoading || $[62] !== nativeSymbol || $[63] !== onWrappingChange || $[64] !== t14 || $[65] !== wrappableNativeBalance) {
    t15 = <WrapSegment id="position-manager-vault-v2-deposit" enabled={true} isWNative={isWNativeVault} wrappableNativeBalance={wrappableNativeBalance} isNativeBalancePending={isNativeBalancePending} wrappedSymbol={t14} nativeSymbol={nativeSymbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[58] = isNativeBalancePending;
    $[59] = isWNativeVault;
    $[60] = isWrapping;
    $[61] = isWrappingToggleLoading;
    $[62] = nativeSymbol;
    $[63] = onWrappingChange;
    $[64] = t14;
    $[65] = wrappableNativeBalance;
    $[66] = t15;
  } else {
    t15 = $[66];
  }
  let t16;
  if ($[67] !== finalPosition || $[68] !== userVaultAssets || $[69] !== vaultData) {
    t16 = <VaultV2TransactionInfoCard vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} />;
    $[67] = finalPosition;
    $[68] = userVaultAssets;
    $[69] = vaultData;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== bundleCanTransfer) {
    t17 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[71] = bundleCanTransfer;
    $[72] = t17;
  } else {
    t17 = $[72];
  }
  let t18;
  if ($[73] !== exitBundleCanTransfer) {
    t18 = !exitBundleCanTransfer && <BundleAuthorizationCallout variant="warning" message={EXIT_BUNDLE_NOT_ENABLED_MESSAGE} />;
    $[73] = exitBundleCanTransfer;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  const t19 = disabledLabel ?? "Review Deposit";
  let t20;
  if ($[75] !== tooltipMessage) {
    t20 = tooltipMessage ? <span className="text-3xs" style={tooltipMessage.textStyle}>{tooltipMessage.message}</span> : undefined;
    $[75] = tooltipMessage;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  const t21 = !!disabledLabel || !bundleCanTransfer;
  let t22;
  if ($[77] !== isInputPending || $[78] !== isLoading || $[79] !== openReviewDialog || $[80] !== t19 || $[81] !== t20 || $[82] !== t21) {
    t22 = <ActionButtons handleFinalize={openReviewDialog} isLoading={isLoading} finalizeLabel={t19} tooltipContent={t20} isFinalizeDisabled={t21} isInputPending={isInputPending} />;
    $[77] = isInputPending;
    $[78] = isLoading;
    $[79] = openReviewDialog;
    $[80] = t19;
    $[81] = t20;
    $[82] = t21;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== element || $[85] !== t13 || $[86] !== t15 || $[87] !== t16 || $[88] !== t17 || $[89] !== t18 || $[90] !== t22) {
    t23 = <>{element}{t13}{t15}{t16}{t17}{t18}{t22}</>;
    $[84] = element;
    $[85] = t13;
    $[86] = t15;
    $[87] = t16;
    $[88] = t17;
    $[89] = t18;
    $[90] = t22;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  return t23;
};
export default VaultV2PositionManagerDepositDialogContent;